<template>
  <div class="manage-devices">

    <!-- Header -->
    <div class="dev-header">
      <div class="dev-header-left">
        <div class="dev-header-icon">
          <i class="bi bi-hdd-network"></i>
        </div>
        <div>
          <h5 class="dev-title mb-0">Biometric Devices</h5>
          <small class="dev-sub">Registered ADMS devices that push attendance to this system</small>
        </div>
      </div>
      <button class="btn-add-device" @click="openAdd">
        <i class="bi bi-plus-lg mr-1"></i> Add Device
      </button>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="dev-loading">
      <div class="spinner-border spinner-border-sm text-primary mr-2"></div> Loading...
    </div>

    <!-- Device list -->
    <div v-else-if="devices.length" class="device-list">
      <div v-for="d in devices" :key="d.id" class="device-card">
        <div class="device-card-left">
          <div class="device-icon" :class="d.is_active ? 'icon-active' : 'icon-inactive'">
            <i class="bi bi-cpu"></i>
          </div>
          <div class="device-info">
            <div class="device-name">{{ d.name }}</div>
            <div class="device-meta">
              <span v-if="d.model" class="dmeta-chip"><i class="bi bi-tag mr-1"></i>{{ d.model }}</span>
              <span v-if="d.location" class="dmeta-chip"><i class="bi bi-geo-alt mr-1"></i>{{ d.location }}</span>
              <span class="dmeta-chip"><i class="bi bi-upc-scan mr-1"></i>SN: {{ d.serial_number }}</span>
              <span v-if="d.ip_address" class="dmeta-chip"><i class="bi bi-ethernet mr-1"></i>{{ d.ip_address }}</span>
            </div>
          </div>
        </div>
        <div class="device-card-right">
          <div class="device-seen">
            <template v-if="d.last_seen_at">
              <span class="seen-dot seen-dot-active"></span>
              <span class="seen-label">Last seen {{ timeAgo(d.last_seen_at) }}</span>
            </template>
            <template v-else>
              <span class="seen-dot seen-dot-never"></span>
              <span class="seen-label text-muted">Never connected</span>
            </template>
          </div>
          <span class="status-badge" :class="d.is_active ? 'badge-active' : 'badge-inactive'">
            {{ d.is_active ? 'Active' : 'Inactive' }}
          </span>
          <div class="device-actions">
            <button class="btn-icon" title="Edit" @click="openEdit(d)"><i class="bi bi-pencil"></i></button>
            <button class="btn-icon btn-icon-danger" title="Remove" @click="confirmDelete(d)"><i class="bi bi-trash3"></i></button>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty state -->
    <div v-else class="dev-empty">
      <i class="bi bi-hdd-network" style="font-size:2.5rem;color:#ccc;display:block;margin-bottom:10px"></i>
      <strong>No devices registered</strong>
      <p class="mt-1 text-muted" style="font-size:0.85rem">Add a device to start tracking its connection status.</p>
      <button class="btn-add-device mt-2" @click="openAdd">
        <i class="bi bi-plus-lg mr-1"></i> Add Device
      </button>
    </div>

    <!-- ADMS setup guide -->
    <div class="adms-guide">
      <div class="adms-guide-header">
        <i class="bi bi-info-circle-fill mr-2 text-primary"></i>
        <strong>Device Setup — Cloud Server Setting on the Granding screen</strong>
      </div>
      <div class="adms-guide-body">
        <div class="adms-guide-grid">
          <div class="adms-row"><span class="adms-field">Server Mode</span><span class="adms-val">ADMS</span></div>
          <div class="adms-row"><span class="adms-field">Enable Domain Name</span><span class="adms-val">ON</span></div>
          <div class="adms-row adms-row-highlight"><span class="adms-field">Server Address</span><span class="adms-val adms-val-domain">{{ admsServerAddress }}</span></div>
          <div class="adms-row"><span class="adms-field">Enable Proxy Server</span><span class="adms-val">OFF</span></div>
          <div class="adms-row"><span class="adms-field">Push endpoint</span><span class="adms-val">/iclock/cdata <em class="text-muted">(hardcoded in device firmware)</em></span></div>
        </div>
        <div class="adms-note">
          <i class="bi bi-exclamation-triangle-fill mr-1" style="color:#f59f00"></i>
          <strong>If port 443 fails:</strong> try port <strong>80</strong> — some older Granding firmware does not support HTTPS. The server will still receive the push over HTTP on port 80.
          The SN on the label of each device must match the serial number registered above.
        </div>
      </div>
    </div>

    <!-- Add/Edit Modal -->
    <div v-if="showModal" class="modal-backdrop-custom" @click.self="closeModal">
      <div class="modal-box">
        <div class="modal-box-header">
          <strong>{{ editingId ? 'Edit Device' : 'Add Device' }}</strong>
          <button class="modal-close" @click="closeModal"><i class="bi bi-x-lg"></i></button>
        </div>
        <div class="modal-box-body">
          <div class="form-group">
            <label class="field-label"><span class="req">*</span> Device Name</label>
            <input v-model="form.name" class="form-control" placeholder="e.g. Main Entrance Biometric" />
          </div>
          <div class="form-group">
            <label class="field-label"><span class="req">*</span> Serial Number</label>
            <input v-model="form.serial_number" class="form-control" placeholder="e.g. 5870203160187 (from device label)" />
          </div>
          <div class="form-row">
            <div class="form-group col-md-6">
              <label class="field-label">IP Address</label>
              <input v-model="form.ip_address" class="form-control" placeholder="e.g. 192.168.0.188" />
            </div>
            <div class="form-group col-md-6">
              <label class="field-label">Model</label>
              <input v-model="form.model" class="form-control" placeholder="e.g. Granding FP800" />
            </div>
          </div>
          <div class="form-group">
            <label class="field-label">Location / Description</label>
            <input v-model="form.location" class="form-control" placeholder="e.g. Ground floor entrance" />
          </div>
          <div class="form-group">
            <label class="field-label d-flex align-items-center gap-2">
              <input type="checkbox" v-model="form.is_active" style="width:16px;height:16px" />
              Active
            </label>
          </div>
        </div>
        <div class="modal-box-footer">
          <button class="btn btn-light" @click="closeModal">Cancel</button>
          <button class="btn btn-primary" :disabled="saving" @click="save">
            <span v-if="saving" class="spinner-border spinner-border-sm mr-1"></span>
            {{ editingId ? 'Save Changes' : 'Add Device' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Delete confirm modal -->
    <div v-if="deleteTarget" class="modal-backdrop-custom" @click.self="deleteTarget = null">
      <div class="modal-box modal-box-sm">
        <div class="modal-box-header">
          <strong>Remove Device</strong>
          <button class="modal-close" @click="deleteTarget = null"><i class="bi bi-x-lg"></i></button>
        </div>
        <div class="modal-box-body">
          <p>Remove <strong>{{ deleteTarget.name }}</strong> (SN: {{ deleteTarget.serial_number }}) from registered devices?</p>
          <p class="text-muted" style="font-size:0.83rem">This only removes the registration record. It does not affect attendance logs.</p>
        </div>
        <div class="modal-box-footer">
          <button class="btn btn-light" @click="deleteTarget = null">Cancel</button>
          <button class="btn btn-danger" :disabled="deleting" @click="deleteDevice">
            <span v-if="deleting" class="spinner-border spinner-border-sm mr-1"></span>
            Remove
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import axios from "axios";
import { API_BASE_URL } from "@/config/apiBaseUrl";

const emptyForm = () => ({
  name: "",
  serial_number: "",
  ip_address: "",
  model: "",
  location: "",
  is_active: true,
});

export default {
  name: "ManageDevices",
  data() {
    return {
      loading: false,
      devices: [],
      showModal: false,
      editingId: null,
      form: emptyForm(),
      saving: false,
      deleteTarget: null,
      deleting: false,
    };
  },
  computed: {
    admsServerAddress() {
      try {
        return new URL(API_BASE_URL).host;
      } catch {
        return window.location.host;
      }
    },
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const { data } = await axios.get("api/biometric-devices");
        this.devices = data;
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    openAdd() {
      this.editingId = null;
      this.form = emptyForm();
      this.showModal = true;
    },
    openEdit(d) {
      this.editingId = d.id;
      this.form = {
        name:          d.name,
        serial_number: d.serial_number,
        ip_address:    d.ip_address || "",
        model:         d.model || "",
        location:      d.location || "",
        is_active:     d.is_active,
      };
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
    },
    async save() {
      this.saving = true;
      try {
        if (this.editingId) {
          const { data } = await axios.put(`api/biometric-devices/${this.editingId}`, this.form);
          const idx = this.devices.findIndex((d) => d.id === this.editingId);
          if (idx >= 0) this.$set(this.devices, idx, data);
        } else {
          const { data } = await axios.post("api/biometric-devices", this.form);
          this.devices.push(data);
        }
        this.closeModal();
      } catch (e) {
        const errors = e?.response?.data?.errors;
        const msg = errors
          ? Object.values(errors).flat().join("\n")
          : e?.response?.data?.message
          || (e?.response ? `Server error (${e.response.status})` : "Network error — check that the API is reachable.");
        alert(msg);
      } finally {
        this.saving = false;
      }
    },
    confirmDelete(d) {
      this.deleteTarget = d;
    },
    async deleteDevice() {
      this.deleting = true;
      try {
        await axios.delete(`api/biometric-devices/${this.deleteTarget.id}`);
        this.devices = this.devices.filter((d) => d.id !== this.deleteTarget.id);
        this.deleteTarget = null;
      } catch (e) {
        alert("Failed to delete device.");
      } finally {
        this.deleting = false;
      }
    },
    timeAgo(ts) {
      const diff = Math.floor((Date.now() - new Date(ts)) / 1000);
      if (diff < 60)         return `${diff}s ago`;
      if (diff < 3600)       return `${Math.floor(diff / 60)}m ago`;
      if (diff < 86400)      return `${Math.floor(diff / 3600)}h ago`;
      return `${Math.floor(diff / 86400)}d ago`;
    },
  },
  mounted() {
    this.load();
  },
};
</script>

<style scoped>
.manage-devices { max-width: 860px; margin: 0 auto; }

/* ── Header ─────────────────────────────────────────── */
.dev-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 24px; }
.dev-header-left { display: flex; align-items: center; gap: 12px; }
.dev-header-icon {
  width: 42px; height: 42px; border-radius: 12px;
  background: #e8f0fe; color: #0d6efd;
  display: flex; align-items: center; justify-content: center; font-size: 1.2rem;
}
.dev-title { font-size: 1.1rem; font-weight: 700; color: #1a2340; }
.dev-sub   { font-size: 0.78rem; color: #8a94a6; }

.btn-add-device {
  background: #0d6efd; color: #fff; border: none; border-radius: 9px;
  padding: 8px 16px; font-size: 0.84rem; font-weight: 600; cursor: pointer;
  display: flex; align-items: center; transition: background 0.15s;
}
.btn-add-device:hover { background: #0b5ed7; }

/* ── Device list ────────────────────────────────────── */
.device-list { display: flex; flex-direction: column; gap: 10px; margin-bottom: 24px; }

.device-card {
  background: #fff; border: 1.5px solid #e9ecef; border-radius: 14px;
  padding: 16px 18px; display: flex; align-items: center; justify-content: space-between;
  gap: 12px; transition: box-shadow 0.15s;
}
.device-card:hover { box-shadow: 0 3px 12px rgba(0,0,0,0.06); }

.device-card-left  { display: flex; align-items: flex-start; gap: 14px; flex: 1; min-width: 0; }
.device-card-right { display: flex; align-items: center; gap: 12px; flex-shrink: 0; }

.device-icon {
  width: 42px; height: 42px; border-radius: 10px; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center; font-size: 1.2rem;
}
.icon-active   { background: #d1e7dd; color: #198754; }
.icon-inactive { background: #f1f3f5; color: #adb5bd; }

.device-name { font-size: 0.95rem; font-weight: 700; color: #1a2340; margin-bottom: 5px; }
.device-meta { display: flex; flex-wrap: wrap; gap: 5px; }
.dmeta-chip {
  font-size: 0.72rem; background: #f8f9fa; border: 1px solid #e9ecef;
  border-radius: 99px; padding: 2px 9px; color: #6c757d;
  display: inline-flex; align-items: center;
}

.device-seen { display: flex; align-items: center; gap: 5px; white-space: nowrap; }
.seen-dot {
  width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0;
}
.seen-dot-active { background: #198754; animation: pulse-g 2s ease-in-out infinite; }
.seen-dot-never  { background: #dee2e6; }
.seen-label { font-size: 0.75rem; color: #6c757d; }

@keyframes pulse-g {
  0%, 100% { box-shadow: 0 0 0 0 rgba(25,135,84,0.4); }
  50%       { box-shadow: 0 0 0 4px rgba(25,135,84,0); }
}

.status-badge {
  font-size: 0.72rem; font-weight: 600; padding: 3px 10px; border-radius: 99px;
}
.badge-active   { background: #d1e7dd; color: #155724; }
.badge-inactive { background: #f1f3f5; color: #6c757d; }

.device-actions { display: flex; gap: 4px; }
.btn-icon {
  width: 32px; height: 32px; border-radius: 7px; border: 1.5px solid #e9ecef;
  background: #fff; cursor: pointer; display: flex; align-items: center; justify-content: center;
  color: #6c757d; font-size: 0.85rem; transition: all 0.15s;
}
.btn-icon:hover { border-color: #0d6efd; color: #0d6efd; background: #e8f0fe; }
.btn-icon-danger:hover { border-color: #dc3545; color: #dc3545; background: #fde8ea; }

/* ── Loading / empty ────────────────────────────────── */
.dev-loading { text-align: center; padding: 40px; color: #6c757d; display: flex; align-items: center; justify-content: center; gap: 8px; }
.dev-empty   { text-align: center; padding: 40px; color: #adb5bd; }

/* ── ADMS guide ─────────────────────────────────────── */
.adms-guide {
  background: #f8f9fa; border: 1.5px solid #e9ecef; border-radius: 14px;
  padding: 18px 20px; margin-top: 8px;
}
.adms-guide-header { display: flex; align-items: center; font-size: 0.9rem; margin-bottom: 14px; }
.adms-guide-grid { display: flex; flex-direction: column; gap: 6px; margin-bottom: 12px; }
.adms-row { display: flex; gap: 16px; font-size: 0.83rem; }
.adms-row-highlight { background: #fff8e1; border-radius: 4px; padding: 2px 6px; margin: 2px -6px; }
.adms-val-domain { font-weight: 700; color: #1a56db; font-family: monospace; }
.adms-field { font-weight: 600; color: #495057; min-width: 160px; }
.adms-val { color: #1a2340; font-family: monospace; }
.adms-note { font-size: 0.78rem; color: #6c757d; border-top: 1px solid #e9ecef; padding-top: 10px; }

/* ── Modal ──────────────────────────────────────────── */
.modal-backdrop-custom {
  position: fixed; inset: 0; background: rgba(0,0,0,0.45);
  display: flex; align-items: center; justify-content: center; z-index: 1050; padding: 16px;
}
.modal-box {
  background: #fff; border-radius: 16px; width: 100%; max-width: 480px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.18); overflow: hidden;
}
.modal-box-sm { max-width: 380px; }
.modal-box-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 16px 20px; border-bottom: 1px solid #f1f3f5; font-size: 0.95rem;
}
.modal-close {
  background: none; border: none; cursor: pointer; color: #6c757d;
  font-size: 1rem; padding: 2px; border-radius: 5px;
}
.modal-close:hover { color: #343a40; }
.modal-box-body   { padding: 20px; }
.modal-box-footer {
  padding: 14px 20px; border-top: 1px solid #f1f3f5;
  display: flex; justify-content: flex-end; gap: 8px;
}
.field-label { font-size: 0.8rem; font-weight: 600; color: #495057; margin-bottom: 4px; display: block; }
.req { color: #dc3545; margin-right: 2px; }
</style>
